import React, { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { BaseLayout } from '@/shared/components/layout';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Checkbox } from '@/shared/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { useQuery } from '@tanstack/react-query';
import { toast } from '@/shared/hooks/use-toast';
import { AlertCircle, CheckCircle2, Loader2, MoreHorizontal, ShieldCheck, UserPlus, XCircle } from 'lucide-react';
import { useUser } from '@/shared/hooks/useUser';
import {
  useMarkStructureTaskModuleCompleteForUser,
  useStructureTaskModules,
  useStructureTasks,
  useStructureTypes,
  useUnmarkStructureTaskModuleCompleteForUser,
  useUpdateStructureTaskModuleRequiredUsers,
} from '../hooks';
import { getClientUsersForTraining } from '../api';
import type { ClientUserOption, StructureTaskModule } from '../types';

const TrainingManagementPage: React.FC = () => {
  const { user, loading } = useUser();
  const { clientName } = useParams<{ clientName: string }>();
  const { data: structureTypes = [], isLoading: isLoadingTypes } = useStructureTypes();
  const { data: tasks = [], isLoading: isLoadingTasks } = useStructureTasks();
  const { data: modules = [], isLoading: isLoadingModules } = useStructureTaskModules();
  const [editingModule, setEditingModule] = useState<StructureTaskModule | null>(null);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [selectedUsersByModule, setSelectedUsersByModule] = useState<Record<string, number[]>>({});

  const canAccessManagement = Boolean(user?.is_client_admin);
  const isLoadingData = isLoadingTypes || isLoadingTasks || isLoadingModules;
  const clientId = user?.client_info?.id ? Number(user.client_info.id) : undefined;

  const updateRequiredUsers = useUpdateStructureTaskModuleRequiredUsers();
  const markCompleteForUser = useMarkStructureTaskModuleCompleteForUser();
  const unmarkCompleteForUser = useUnmarkStructureTaskModuleCompleteForUser();

  const { data: clientUsersData } = useQuery({
    queryKey: ['training-client-users-management', clientId],
    queryFn: async () => {
      if (!clientId) return { users: [] };
      const response = await getClientUsersForTraining(clientId);
      return response.data;
    },
    enabled: Boolean(clientId),
  });
  const clientUsers: ClientUserOption[] = clientUsersData?.users ?? [];
  const getRequiredUserIds = (module: StructureTaskModule): number[] =>
    (module.required_users ?? []).map((requiredUser) => requiredUser.id);

  const availableUsersForEdit = useMemo(() => {
    if (!editingModule) return [];
    const assignedIds = new Set(getRequiredUserIds(editingModule));
    return clientUsers.filter((userOption) => !assignedIds.has(userOption.id));
  }, [clientUsers, editingModule]);

  const handleToggleNewUser = (userId: number) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId],
    );
  };

  const handleOpenAddUsers = (module: StructureTaskModule) => {
    setEditingModule(module);
    setSelectedUserIds([]);
  };

  const getSelectedUsersForModule = (moduleId: string): number[] => selectedUsersByModule[moduleId] ?? [];

  const toggleSelectedUser = (moduleId: string, userId: number) => {
    setSelectedUsersByModule((prev) => {
      const current = prev[moduleId] ?? [];
      const next = current.includes(userId)
        ? current.filter((id) => id !== userId)
        : [...current, userId];
      return {
        ...prev,
        [moduleId]: next,
      };
    });
  };

  const toggleSelectAllUsersForModule = (moduleId: string, allUserIds: number[]) => {
    setSelectedUsersByModule((prev) => {
      const current = prev[moduleId] ?? [];
      const shouldSelectAll = current.length !== allUserIds.length;
      return {
        ...prev,
        [moduleId]: shouldSelectAll ? [...allUserIds] : [],
      };
    });
  };

  const clearModuleSelection = (moduleId: string) => {
    setSelectedUsersByModule((prev) => ({
      ...prev,
      [moduleId]: [],
    }));
  };

  const handleSaveAddedUsers = async () => {
    if (!editingModule) return;
    try {
      const nextRequiredIds = [...getRequiredUserIds(editingModule), ...selectedUserIds];
      await updateRequiredUsers.mutateAsync({
        id: editingModule.id,
        required_user_ids: Array.from(new Set(nextRequiredIds)),
      });
      toast({ title: 'Required users updated.' });
      setEditingModule(null);
      setSelectedUserIds([]);
    } catch (error) {
      void error;
      toast({ title: 'Failed to update required users.', variant: 'destructive' });
    }
  };

  const handleMarkSelectedComplete = async (module: StructureTaskModule) => {
    const selectedIds = getSelectedUsersForModule(module.id);
    if (selectedIds.length === 0) {
      toast({ title: 'Select at least one user first.' });
      return;
    }
    try {
      await Promise.all(
        selectedIds.map((userId) => markCompleteForUser.mutateAsync({ id: module.id, userId })),
      );
      toast({ title: `Marked complete for ${selectedIds.length} user(s).` });
      clearModuleSelection(module.id);
    } catch (error) {
      void error;
      toast({ title: 'Failed to mark selected users complete.', variant: 'destructive' });
    }
  };

  const handleUnmarkSelectedComplete = async (module: StructureTaskModule) => {
    const selectedIds = getSelectedUsersForModule(module.id);
    if (selectedIds.length === 0) {
      toast({ title: 'Select at least one user first.' });
      return;
    }
    try {
      await Promise.all(
        selectedIds.map((userId) => unmarkCompleteForUser.mutateAsync({ id: module.id, userId })),
      );
      toast({ title: `Unmarked completion for ${selectedIds.length} user(s).` });
      clearModuleSelection(module.id);
    } catch (error) {
      void error;
      toast({ title: 'Failed to unmark selected users.', variant: 'destructive' });
    }
  };

  const handleRemoveSelectedUsers = async (module: StructureTaskModule) => {
    const selectedIds = getSelectedUsersForModule(module.id);
    if (selectedIds.length === 0) {
      toast({ title: 'Select at least one user first.' });
      return;
    }
    try {
      const nextRequiredIds = getRequiredUserIds(module).filter(
        (requiredId) => !selectedIds.includes(requiredId),
      );
      await updateRequiredUsers.mutateAsync({
        id: module.id,
        required_user_ids: nextRequiredIds,
      });
      toast({ title: `Removed ${selectedIds.length} user(s) from required list.` });
      clearModuleSelection(module.id);
    } catch (error) {
      void error;
      toast({ title: 'Failed to remove selected users.', variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <BaseLayout>
        <div className="mx-auto max-w-4xl p-6">
          <p className="text-sm text-muted-foreground">Loading training management...</p>
        </div>
      </BaseLayout>
    );
  }

  if (!canAccessManagement) {
    return (
      <BaseLayout>
        <div className="mx-auto max-w-4xl p-6">
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900">
            <div className="mb-2 flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              <h1 className="text-sm font-semibold">Access restricted</h1>
            </div>
            <p className="text-sm">
              Training management is only available to client admins.
            </p>
          </div>
        </div>
      </BaseLayout>
    );
  }

  if (isLoadingData) {
    return (
      <BaseLayout>
        <div className="mx-auto max-w-6xl p-6">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading assignment data...
          </div>
        </div>
      </BaseLayout>
    );
  }

  return (
    <BaseLayout>
      <div className="mx-auto max-w-6xl space-y-6 p-4 sm:p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="mb-2 flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-primary" />
              <h1 className="text-2xl font-bold tracking-tight">Training Management</h1>
            </div>
            <p className="text-sm text-muted-foreground">
              View required assignments and watch completion by module.
            </p>
          </div>
          {clientName && (
            <Button asChild variant="outline" className="w-full sm:w-auto">
              <Link to={`/app/training/${clientName}`}>View Modules</Link>
            </Button>
          )}
        </div>

        {modules.length === 0 ? (
          <div className="rounded-xl border border-dashed p-8 text-center">
            <p className="text-sm text-muted-foreground">No training modules found.</p>
          </div>
        ) : (
          <div className="space-y-5">
            {structureTypes.map((structureType) => {
              const typeTasks = tasks.filter((task) => task.structure_type === structureType.id);
              if (typeTasks.length === 0) return null;

              return (
                <div key={structureType.id} className="rounded-xl border bg-card shadow-sm overflow-hidden">
                  <div className="border-b bg-muted/40 px-4 py-3">
                    <h2 className="text-base font-semibold">{structureType.name}</h2>
                  </div>

                  <div className="space-y-4 p-4">
                    {typeTasks.map((task) => {
                      const taskModules = modules.filter((module) => module.task === task.id);
                      if (taskModules.length === 0) return null;

                      return (
                        <div key={task.id} className="space-y-3">
                          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                            <h3 className="text-sm font-semibold text-foreground">{task.name}</h3>
                            <Badge variant="secondary" className="text-xs">
                              {taskModules.length} {taskModules.length === 1 ? 'module' : 'modules'}
                            </Badge>
                          </div>

                          <div className="space-y-3">
                            {taskModules.map((module) => {
                              const requiredStatus = module.required_watch_status ?? [];
                              const requiredUserIds = requiredStatus.map((status) => status.user_id);
                              const selectedIds = getSelectedUsersForModule(module.id);
                              const allSelected =
                                requiredUserIds.length > 0 && selectedIds.length === requiredUserIds.length;
                              const assignedCount = requiredStatus.length;
                              const watchedCount = requiredStatus.filter((status) => status.watched).length;

                              return (
                                <div key={module.id} className="rounded-lg border bg-card p-4">
                                  <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                    <div className="flex flex-wrap items-center gap-2">
                                      <h4 className="text-sm font-semibold">{module.name}</h4>
                                      <Badge variant={assignedCount > 0 ? 'default' : 'outline'}>
                                        {assignedCount > 0
                                          ? `${watchedCount}/${assignedCount} watched`
                                          : 'No required users'}
                                      </Badge>
                                    </div>
                                    <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="w-full gap-1 sm:w-auto"
                                        onClick={() => handleOpenAddUsers(module)}
                                      >
                                        <UserPlus className="h-4 w-4" />
                                        Add Users
                                      </Button>
                                      <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                          <Button variant="outline" size="sm" className="w-full gap-1 sm:w-auto">
                                            <MoreHorizontal className="h-4 w-4" />
                                            Actions{selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}
                                          </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end">
                                          <DropdownMenuItem
                                            disabled={selectedIds.length === 0}
                                            onClick={() => handleMarkSelectedComplete(module)}
                                          >
                                            Mark selected complete
                                          </DropdownMenuItem>
                                          <DropdownMenuItem
                                            disabled={selectedIds.length === 0}
                                            onClick={() => handleUnmarkSelectedComplete(module)}
                                          >
                                            Unmark selected complete
                                          </DropdownMenuItem>
                                          <DropdownMenuItem
                                            disabled={selectedIds.length === 0}
                                            onClick={() => handleRemoveSelectedUsers(module)}
                                            className="text-destructive"
                                          >
                                            Remove selected from required users
                                          </DropdownMenuItem>
                                        </DropdownMenuContent>
                                      </DropdownMenu>
                                    </div>
                                  </div>
                                  {module.description && (
                                    <p className="mb-3 text-sm text-muted-foreground">{module.description}</p>
                                  )}
                                  {requiredStatus.length > 0 ? (
                                    <div className="space-y-2">
                                      <label className="mb-1 flex items-center gap-2 rounded-md border bg-muted/20 px-2 py-1.5 text-xs text-muted-foreground">
                                        <Checkbox
                                          checked={allSelected}
                                          onCheckedChange={() =>
                                            toggleSelectAllUsersForModule(module.id, requiredUserIds)
                                          }
                                        />
                                        Select all users in this module
                                      </label>
                                      {requiredStatus.map((status) => (
                                        <div
                                          key={status.user_id}
                                          className="flex flex-col gap-2 rounded-md border bg-muted/30 p-2 sm:flex-row sm:items-center sm:justify-between"
                                        >
                                          <div className="flex min-w-0 items-start gap-2">
                                            <Checkbox
                                              checked={selectedIds.includes(status.user_id)}
                                              onCheckedChange={() =>
                                                toggleSelectedUser(module.id, status.user_id)
                                              }
                                            />
                                            <div className="min-w-0">
                                              <p className="text-sm font-medium">{status.name}</p>
                                              <p className="break-all text-xs text-muted-foreground">{status.email}</p>
                                            </div>
                                          </div>
                                          <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                                            {status.watched ? (
                                              <>
                                                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                                                <span className="text-xs text-emerald-700">
                                                  Watched
                                                  {status.watched_at
                                                    ? ` (${new Date(status.watched_at).toLocaleString()})`
                                                    : ''}
                                                </span>
                                              </>
                                            ) : (
                                              <>
                                                <XCircle className="h-4 w-4 text-amber-600" />
                                                <span className="text-xs text-amber-700">Pending</span>
                                              </>
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <p className="text-sm text-muted-foreground">
                                      No users are assigned to this module yet.
                                    </p>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={Boolean(editingModule)} onOpenChange={(open) => !open && setEditingModule(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Required Users</DialogTitle>
            <DialogDescription>
              Select additional users to require for this module.
            </DialogDescription>
          </DialogHeader>
          {availableUsersForEdit.length === 0 ? (
            <p className="text-sm text-muted-foreground">All client users are already assigned.</p>
          ) : (
            <div className="max-h-64 space-y-2 overflow-y-auto pr-1">
              {availableUsersForEdit.map((userOption) => (
                <label
                  key={userOption.id}
                  className="flex items-center justify-between rounded-md border p-2"
                >
                  <div className="text-sm">
                    <p className="font-medium">
                      {userOption.first_name} {userOption.last_name}
                    </p>
                    <p className="text-xs text-muted-foreground">{userOption.email}</p>
                  </div>
                  <Checkbox
                    checked={selectedUserIds.includes(userOption.id)}
                    onCheckedChange={() => handleToggleNewUser(userOption.id)}
                  />
                </label>
              ))}
            </div>
          )}
          <div className="mt-3 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setEditingModule(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveAddedUsers}
              disabled={!editingModule || selectedUserIds.length === 0 || updateRequiredUsers.isPending}
            >
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </BaseLayout>
  );
};

export default TrainingManagementPage;
